<template>
  <div>
    <div class="sticky top-0 z-10 bg-white">
      <h1 class="text-left text-xl font-medium text-primary">
        Frameworks
      </h1>

      <section class="mx-auto mt-4 w-[98%]">
        <InputWithIcon
          :val="frameworkQuery"
          classes="py-2 text-sm rounded-lg"
          placeholder="Search frameworks..."
          @input="frameworkQuery = $event"
        >
          <template #icon>
            <SearchIcon />
          </template>
        </InputWithIcon>
      </section>
      <!-- Selected frameworks summary -->
      <section class="mt-4 flex items-center gap-3.5 pb-1.5 text-sm">
        <div class="flex items-center gap-1.5">
          <input
            id="selectAllLatest"
            ref="select-all-latest-frameworks-checkbox"
            type="checkbox"
            class="cursor-pointer rounded-sm border-2 border-gray-400 bg-white p-1.5 text-main-primary hover:border-gray-500 focus:border-main-primary focus:ring-transparent"
            @change="selectAllLatestVersions"
          >
          <label
            for="select-all-latest-frameworks-checkbox"
            class="text-[#68737D]"
          >Select All Latest</label>
        </div>
        <span class="text-[#68737D]">{{ selectedFrameworks.length }} Selected</span>
      </section>
    </div>
    <!-- Frameworks -->
    <section class="mt-4 flex flex-col gap-4 p-2">
      <EsgFrameworkPickerAccordion
        v-for="framework in filteredFrameworks"
        :ref="framework.short_form"
        :key="framework.short_form"
        :framework="framework"
        @selection-changed="handleSelection($event, framework.short_form)"
        @framework-version-selected="
          handleVersionSelection($event, framework.short_form)
        "
        @framework-version-unselected="
          handleVersionSelection($event, framework.short_form)
        "
        @deselect-all-latest="handleDeselectAllLatest"
        @opened="hideOtherAccordions(framework.short_form)"
      />
    </section>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import {
  frameworksInjectionKey,
  savedFrameworksInjectionKey,
} from '@/utils/keys'

export default {
  name: 'EsgFrameworksPicker',

  inject: { frameworksInjectionKey, savedFrameworksInjectionKey },

  props: {
    frameworks: {
      type: Array,
      default() {
        return []
      },
    },
    savedFrameworks: {
      type: Array,
      default() {
        return []
      },
    },
  },

  emits: ['versionSelected'],

  data() {
    const frameworks = [
      ...(this.frameworks.length
        ? this.frameworks
        : this.frameworksInjectionKey),
    ].map((framework) => {
      framework.versions = framework.versions.map((version) => {
        version.isChecked = (
          this.savedFrameworks.length
            ? this.savedFrameworks
            : this.savedFrameworksInjectionKey
        ).includes(version.id)
        return version
      })
      return framework
    })

    return {
      frameworkQuery: '',
      filteredFrameworks: frameworks,
      allFrameworks: frameworks,
      selectedFrameworks: [],
    }
  },

  watch: {
    frameworkQuery(newQuery) {
      if (!newQuery) {
        this.filteredFrameworks = this.allFrameworks
      }
      else {
        this.filteredFrameworks = [...this.allFrameworks].filter(
          ({ name, short_form }) =>
            name.toLowerCase().includes(newQuery.toLowerCase())
            || short_form.toLowerCase().includes(newQuery.toLowerCase()),
        )
      }
    },
  },

  methods: {
    handleSelection(isSelected, shortForm) {
      if (isSelected && !this.selectedFrameworks.includes(shortForm)) {
        this.selectedFrameworks.push(shortForm)
      }
      else {
        const deleteIndex = this.selectedFrameworks.indexOf(shortForm)
        if (deleteIndex !== -1) {
          this.selectedFrameworks.splice(deleteIndex, 1)
        }
      }
    },
    hideOtherAccordions(openedFrameworkShortform) {
      setTimeout(() => {
        this.filteredFrameworks.forEach(({ short_form }) => {
          if (short_form !== openedFrameworkShortform) {
            this.$refs[short_form][0].closePanel()
          }
        })
      }, 250)
    },
    handleVersionSelection: debounce(function (newVersion, shortForm) {
      // Update allFrameworks used for searching, to maintain checkedStatuses
      this.allFrameworks = this.allFrameworks.map((framework) => {
        if (framework.short_form === shortForm) {
          framework.versions = framework.versions.map((originalVersion) => {
            return originalVersion.id === newVersion.id
              ? newVersion
              : originalVersion
          })
        }
        return framework
      })

      // Fire event to be used in the child component to be used by the metric picker instantly
      this.$emit('versionSelected', this.allFrameworks)
    }, 100),
    selectAllLatestVersions(event) {
      if (event.target.checked) {
        this.filteredFrameworks.forEach(({ short_form }) => {
          this.$refs[short_form][0].selectLatest()
        })
      }
      else {
        this.filteredFrameworks.forEach(({ short_form }) => {
          this.$refs[short_form][0].deselectLatest()
        })
      }
    },
    handleDeselectAllLatest() {
      this.$refs['select-all-latest-frameworks-checkbox'].checked = false
    },
  },
}
</script>
